import { Button, Select } from '@mantine/core';
import {
  IconCalendar,
  IconClipboardTextFilled,
  IconFileInvoiceFilled,
  IconFileSpreadsheet,
  IconFileTextFilled,
  IconFileTypePdf,
  IconHexagonFilled,
  IconShield,
  IconUserFilled,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { browserPdfOutput, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { PmsReportLinks } from '../pms/ReportsTab.js';
import orders from '../purchasing/Orders.module.css';
import { pricesVisible } from '../purchasing/orders.js';
import classes from '../purchasing/Purchasing.module.css';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { formatCell, reportPdf } from './report-pdf.js';
import {
  type Cell,
  REPORT_CARDS,
  type ReportCardKey,
  type ReportsRoute,
  reportFileName,
  reportStatuses,
  reportsPath,
  reportTable,
  statusKey,
} from './reports.js';
import type { ReportData, ReportKind, ReportQuery, ReportSource } from './types.js';
import { reportWorkbook, saveReportFile, XLSX_TYPE } from './xlsx.js';

const ICONS: Record<ReportCardKey, ReactNode> = {
  requisitions: <IconFileTextFilled size={22} />,
  orders: <IconFileInvoiceFilled size={22} />,
  supplierPerformance: <IconUserFilled size={22} />,
  stock: <IconHexagonFilled size={22} />,
  consumption: <IconShield size={22} stroke={2.4} />,
  costAnalysis: <IconClipboardTextFilled size={22} />,
};

const PAGE_SIZE = 20;

export interface ReportsScreenProps {
  source: ReportSource;
  /** Sorgu dizesinden (`?report=orders&from=…`; `reportsRoute`). */
  route: ReportsRoute;
  /** PMS raporlarının bölümü (plan M7 Task 31): History ▸ Reports'a bağlantılar. */
  pmsReports?: boolean;
}

/**
 * Raporlar (M12c Task 18; `satin-alma-akisi.jpeg` "Reports"): iki satır üç kart; dört rapor
 * hazır, Supplier Performance ve Cost Analysis hazırlanıyor (ADR 0040 #189). Rapor görünümü:
 * gemi (ofiste), gün aralığı ve durum süzgeçleri; sıralanabilir, sayfalı tablo; özet; Excel ve PDF
 * istemcide (iki uygulamada aynı; gemi internetsiz).
 */
export function ReportsScreen({ source, route, pmsReports = false }: ReportsScreenProps) {
  const shell = useShell();
  if (route.view === 'report') {
    return (
      <ReportView
        key={route.report}
        source={source}
        report={route.report}
        query={route.query}
        onQuery={(query) =>
          shell?.navigate(reportsPath({ view: 'report', report: route.report, query }))
        }
        onBack={() => shell?.navigate(reportsPath({ view: 'cards' }))}
      />
    );
  }
  return (
    <ReportCards
      pmsReports={pmsReports}
      onOpen={(report) => shell?.navigate(reportsPath({ view: 'report', report, query: {} }))}
    />
  );
}

function ReportCards({
  onOpen,
  pmsReports,
}: {
  onOpen: (report: ReportKind) => void;
  pmsReports: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.screen}>
      <PageHeader title={t('reports.title')} description={t('reports.description')} />
      <Panel flush aria-label={t('reports.list')}>
        <ul className={orders.reportGrid} aria-label={t('reports.list')}>
          {REPORT_CARDS.map((card) => {
            const title = t(`reports.cards.${card.key}.title`);
            const body = (
              <>
                <span className={orders.reportIcon} aria-hidden>
                  {ICONS[card.key]}
                </span>
                <span className={orders.reportTitle}>
                  {title}
                  {card.ready ? null : (
                    <StatusBadge tone="neutral">{t('reports.soon')}</StatusBadge>
                  )}
                </span>
                <span className={orders.reportText}>{t(`reports.cards.${card.key}.text`)}</span>
              </>
            );
            return (
              <li key={card.key} className={orders.reportItem}>
                {card.ready ? (
                  <button
                    type="button"
                    className={orders.reportCard}
                    onClick={() => onOpen(card.key as ReportKind)}
                  >
                    {body}
                  </button>
                ) : (
                  <div className={orders.reportCard} aria-disabled="true">
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Panel>
      {pmsReports ? <PmsReportLinks /> : null}
    </div>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: ReportData; vessels: PersonnelVessel[] };

function ReportView({
  source,
  report,
  query,
  onQuery,
  onBack,
}: {
  source: ReportSource;
  report: ReportKind;
  query: ReportQuery;
  onQuery: (query: ReportQuery) => void;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`reports.view.${key}`, options);
  const title = t(`reports.cards.${report}.title`);
  const tr = useMemo<Translate>(() => (key, options) => t(key, options ?? {}), [t]);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<string> | null>(null);
  const [busy, setBusy] = useState<'xlsx' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState(false);
  const { vessel = null, from = null, to = null, status = null } = query;

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [data, vessels] = await Promise.all([
        source.get(report, { vessel, from, to, status }),
        source.vessels(),
      ]);
      setState({ kind: 'ready', data, vessels });
      setPage(1);
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, report, vessel, from, to, status]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  // Gemide PO raporu fiyatsız (ADR 0169 §2): ekran, Excel ve PDF aynı tablodan
  const prices = pricesVisible(source.scope);
  const full = useMemo(
    () => (ready ? reportTable(ready.data, tr, { prices }) : null),
    [ready, tr, prices],
  );
  // Ekranda tek para birimi varsa sütunu yerine tutar başlığında (ör. "Total (USD)"); dışa
  // aktarmada sütun kalır
  // Gemide tek gemi var: "Vessel" sütunu ekranda gösterilmez (dışa aktarmada kalır)
  const ship = source.scope === 'ship';
  const view = useMemo(() => {
    if (!full) return null;
    const currencyAt = full.columns.findIndex((column) => column.key === 'currency');
    const currencies = new Set(full.rows.map((row) => row[currencyAt]));
    const single = currencyAt >= 0 && currencies.size === 1 ? String([...currencies][0]) : null;
    const hidden = new Set(
      full.columns
        .map((column, i) => ({ column, i }))
        .filter(
          ({ column }) =>
            (single !== null && column.key === 'currency') || (ship && column.key === 'vessel'),
        )
        .map(({ i }) => i),
    );
    if (hidden.size === 0) return full;
    return {
      ...full,
      columns: full.columns
        .filter((_, i) => !hidden.has(i))
        .map((column) =>
          single !== null && column.kind === 'money'
            ? { ...column, label: `${column.label} (${single})` }
            : column,
        ),
      rows: full.rows.map((row) => row.filter((_, i) => !hidden.has(i))),
    };
  }, [full, ship]);
  const rows = useMemo(() => {
    if (!view) return [];
    if (!sort) return view.rows;
    const index = view.columns.findIndex((column) => column.key === sort.key);
    return sortItems(view.rows, sort.dir, (row: Cell[]) => row[index] ?? null);
  }, [view, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const statuses = reportStatuses(report, source.scope === 'ship' ? 'ship' : 'office');
  const stock = report === 'stock';
  const change = (patch: Partial<ReportQuery>) => {
    const next = { ...query, ...patch };
    onQuery(
      Object.fromEntries(Object.entries(next).filter(([, value]) => Boolean(value))) as ReportQuery,
    );
  };
  const period = ready
    ? stock
      ? k('stockOn', { date: formatDay(ready.data.to) })
      : k('period', { from: formatDay(ready.data.from), to: formatDay(ready.data.to) })
    : '';
  const summary = ready ? summaryItems(ready.data, tr, locale, { prices }) : [];

  const exportAs = async (kind: 'xlsx' | 'pdf') => {
    if (!ready || !full || !view) return;
    setBusy(kind);
    setExportError(false);
    const data = ready.data;
    // Dışa aktarma bütün sütunlarla, ekrandaki sırayla
    const order = sort ? full.columns.findIndex((column) => column.key === sort.key) : -1;
    const sorted = {
      ...full,
      rows:
        order < 0 || !sort
          ? full.rows
          : sortItems(full.rows, sort.dir, (row: Cell[]) => row[order] ?? null),
    };
    const generated = k('generated', {
      at: `${data.generatedAt.slice(0, 16).replace('T', ' ')} UTC`,
    });
    try {
      if (kind === 'xlsx') {
        const bytes = await reportWorkbook(sorted, { title, period, generated });
        await saveReportFile(source, reportFileName(report, data, 'xlsx'), bytes, XLSX_TYPE);
      } else {
        const company = (await source.brandName?.()) ?? null;
        const definition = reportPdf(
          {
            title,
            period,
            company,
            generatedAt: data.generatedAt,
            table: sorted,
            summary,
            locale,
          },
          tr,
        );
        const output = source.pdf ?? browserPdfOutput();
        await output.save(reportFileName(report, data, 'pdf'), () => renderPdf(definition));
      }
    } catch {
      setExportError(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={classes.screen}>
      <PageHeader
        title={title}
        description={t(`reports.cards.${report}.text`)}
        crumb={title}
        actions={
          <div className={classes.headerActions}>
            <Button
              variant="default"
              leftSection={<IconFileSpreadsheet size={16} />}
              loading={busy === 'xlsx'}
              disabled={!ready}
              onClick={() => void exportAs('xlsx')}
            >
              {k('excel')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} />}
              loading={busy === 'pdf'}
              disabled={!ready}
              onClick={() => void exportAs('pdf')}
            >
              {k('pdf')}
            </Button>
          </div>
        }
      />
      {exportError ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {k('exportError')}
        </p>
      ) : null}

      <Panel flush aria-label={title}>
        <div className={classes.toolbar} data-single>
          <form
            className={orders.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            <Select
              label={k('vessel')}
              data={[
                ...(ship ? [] : [{ value: '', label: k('allVessels') }]),
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={ship ? (ready?.vessels[0]?.id ?? null) : (vessel ?? '')}
              allowDeselect={false}
              readOnly={ship}
              {...(ship ? { rightSection: null } : {})}
              onChange={(value) => change({ vessel: value || null })}
            />
            {statuses.length > 0 ? (
              <Select
                label={k('status')}
                data={[
                  { value: '', label: k('allStatuses') },
                  ...statuses.map((value) => ({ value, label: t(statusKey(report, value)) })),
                ]}
                value={status ?? ''}
                allowDeselect={false}
                onChange={(value) => change({ status: value || null })}
              />
            ) : null}
            {stock ? null : (
              <fieldset className={classes.range}>
                <legend className={classes.label}>{k('dateRange')}</legend>
                <div className={classes.rangeBox}>
                  <IconCalendar size={16} aria-hidden />
                  <DayInput
                    variant="unstyled"
                    size="xs"
                    rightSection={null}
                    aria-label={k('from')}
                    value={ready?.data.from ?? from}
                    maxDate={ready?.data.to ?? to ?? undefined}
                    onChange={(value) => change({ from: value })}
                  />
                  <span aria-hidden>–</span>
                  <DayInput
                    variant="unstyled"
                    size="xs"
                    rightSection={null}
                    aria-label={k('to')}
                    value={ready?.data.to ?? to}
                    minDate={ready?.data.from ?? from ?? undefined}
                    onChange={(value) => change({ to: value })}
                  />
                </div>
              </fieldset>
            )}
          </form>
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : !ready || !view ? (
          <TableSkeleton columns={8} label={k('loading')} />
        ) : view.rows.length === 0 ? (
          <EmptyState title={k('empty')} description={k('emptyText')} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table} ${orders.reportTable}`}>
                <thead>
                  <tr>
                    {view.columns.map((column, index) => (
                      <SortableTh
                        key={column.key}
                        column={column.key}
                        label={column.label}
                        sort={sort ?? { key: '', dir: 'asc' }}
                        onSort={setSort}
                        numeric={column.kind === 'money' || column.kind === 'number'}
                        {...(index === 0 ? { className: classes.key } : {})}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.join('\u0001')}>
                      {view.columns.map((column, index) => (
                        <td
                          key={column.key}
                          data-numeric={
                            column.kind === 'money' || column.kind === 'number' || undefined
                          }
                          className={index === 0 ? classes.key : classes.nowrap}
                        >
                          {formatCell(column.kind, row[index] ?? null, locale)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, rows.length),
                total: rows.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
        {ready ? (
          <dl className={orders.summary} aria-label={k('summary')}>
            <div className={orders.summaryItem}>
              <dt>{stock ? k('stockDate') : k('dateRange')}</dt>
              <dd>
                {stock
                  ? formatDay(ready.data.to)
                  : k('period', { from: formatDay(ready.data.from), to: formatDay(ready.data.to) })}
              </dd>
            </div>
            {summary.map((item) => (
              <div key={item.label} className={orders.summaryItem}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Panel>

      <div>
        <Button variant="subtle" onClick={onBack}>
          {k('back')}
        </Button>
      </div>
    </div>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Raporun özet satırları: ekranda ve PDF'te aynı. */
export function summaryItems(
  data: ReportData,
  t: Translate,
  locale: string,
  options: { prices?: boolean } = {},
): { label: string; value: string }[] {
  const s = (key: string, options: Record<string, unknown> = {}) =>
    t(`reports.summary.${key}`, options);
  const money = (value: number) => formatCell('money', value, locale);
  switch (data.report) {
    case 'requisitions':
      return [
        { label: s('requisitions'), value: String(data.summary.count) },
        {
          label: s('total', { currency: data.summary.total.currency }),
          value: money(data.summary.total.value),
        },
        ...(data.summary.total.unpriced > 0
          ? [{ label: s('unpriced'), value: String(data.summary.total.unpriced) }]
          : []),
      ];
    case 'orders':
      return [
        { label: s('orders'), value: String(data.summary.count) },
        // Gemide tutar yok (ADR 0169 §2)
        ...(options.prices === false ? [] : data.summary.byCurrency).flatMap((entry) => [
          { label: s('ordersTotal', { currency: entry.currency }), value: money(entry.total) },
          { label: s('received', { currency: entry.currency }), value: money(entry.received) },
          { label: s('remaining', { currency: entry.currency }), value: money(entry.remaining) },
        ]),
      ];
    case 'stock':
      return [
        { label: s('items'), value: String(data.summary.items) },
        { label: s('low'), value: String(data.summary.low) },
        { label: s('critical'), value: String(data.summary.critical) },
      ];
    case 'consumption':
      return [
        { label: s('materials'), value: String(data.summary.materials) },
        { label: s('movements'), value: String(data.summary.movements) },
      ];
  }
}
